import { useAccountsService } from '@restworld/data-services';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { setIsValidateOtp, setIsVerificationSuccessful } from '../components/phone-number/states';
import { useAuthContext } from '@restworld/feature-authentication';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@restworld/utils-common';
import { smsSentCountProfileAtom } from './use-verify-phone-number-form';

export interface OtpCodeInputForm {
  otpCode: number;
}

export function useVerifyOtpForm() {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    setError,
  } = useForm<OtpCodeInputForm>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serverValidationFailed, setServerValidationFailed] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const acc = useAccountsService();
  const setSuccessfulVerificationContent = useSetAtom(setIsVerificationSuccessful);
  const setValidateOtpContent = useSetAtom(setIsValidateOtp);
  const authContext = useAuthContext();
  const { t } = useTranslation('user-settings');
  const { trackEvent } = useAnalytics();
  const smsSentCountProfile = useAtomValue(smsSentCountProfileAtom);

  const handleValidation = useCallback(
    async (data: OtpCodeInputForm) => {
      setLoading(true);
      try {
        await acc.verifyPhoneOtp({ otpCode: data.otpCode.toString() });
        setIsSuccess(true);
        authContext?.initialize();
        setLoading(false);
        trackEvent('worker_request_otp_for_phone_number_change', {
          successful_otp_validation: true,
          sms_sent_count: smsSentCountProfile,
        });
        setValidateOtpContent(false);
        setSuccessfulVerificationContent(true);
      } catch (error) {
        setLoading(false);
        setValue('otpCode', null);
        setError('otpCode', { message: t('validate_otp_content_code_failed_on_server') });
        setServerValidationFailed(true);
      }
    },
    [
      acc,
      authContext,
      trackEvent,
      smsSentCountProfile,
      setValidateOtpContent,
      setSuccessfulVerificationContent,
      setValue,
      setError,
      t,
    ]
  );

  const onSubmit = async (data: OtpCodeInputForm) => {
    handleValidation(data);
  };

  return {
    loading,
    onSubmit,
    register,
    handleSubmit,
    watch,
    control,
    errors,
    setValue,
    setError,
    serverValidationFailed,
    setServerValidationFailed,
    isSuccess,
  };
}
